/**
 * Subscribes feed track layers to the map: renders track data as it arrives
 * and applies layer visibility toggles. Layer ids come from active plugins'
 * map layers and equal their WebSocket event types.
 */

import { useEffect } from 'react';
import type { Track } from '@vtak/shared';
import type { MapInstance, ViewMode } from '@/map/map-setup';
import { usePluginStore } from '@/stores/plugin-store';
import { usePluginDataStore } from '@/stores/plugin-data-store';
import { useLayerVisibilityStore } from '@/stores/layer-visibility-store';
import { useTrackClassificationStore } from '@/stores/track-classification-store';
import { useTrackFilterStore } from '@/stores/track-filter-store';
import { useTimelineStore } from '@/stores/timeline-store';
import { useMapStore } from '@/stores/map-store';
import { buildActiveMapLayers, type ActiveMapLayer } from '@/components/map/plugin-layer-manager';
import { renderLayer } from '@/map/markers/track-layer-controller';
import { setLayerVisibility } from '@/map/markers';

/** Last-rendered data per layer, to skip redundant renders. */
const lastRendered = new Map<string, unknown[]>();

/** Subscribe feed track layers to the map (render + visibility). */
export function useTrackLayers(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): void {
  useEffect(() => {
    seedVisibility();
    const unsubPlugins = usePluginStore.subscribe(seedVisibility);
    const unsubData = subscribeData(mapRef, modeRef);
    const unsubVisibility = subscribeVisibility(mapRef, modeRef);
    const unsubClassification = subscribeClassification(mapRef, modeRef);
    const unsubFilter = subscribeFilter(mapRef, modeRef);
    const unsubViewMode = subscribeViewMode(mapRef, modeRef);

    return () => {
      unsubPlugins();
      unsubData();
      unsubVisibility();
      unsubClassification();
      unsubFilter();
      unsubViewMode();
    };
  }, []);
}

/** All feed track layers from active plugins. */
function trackLayers(): ActiveMapLayer[] {
  return buildActiveMapLayers(usePluginStore.getState().activePlugins);
}

/** The set of track layer ids (each equals a wsEvent type). */
function trackLayerIds(): Set<string> {
  return new Set(trackLayers().map((layer) => layer.fullId));
}

/** Seed visibility defaults from active plugin map layers. */
function seedVisibility(): void {
  const seed = useLayerVisibilityStore.getState().seed;
  for (const layer of trackLayers()) {
    seed(layer.fullId, layer.definition.defaultVisible);
  }
}

/** Re-render track layers whose plugin data changed. */
function subscribeData(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): () => void {
  return usePluginDataStore.subscribe((state) => {
    const map = mapRef.current;
    if (!map) return;
    for (const layerId of trackLayerIds()) {
      renderIfChanged(map, modeRef.current, layerId, state.data.get(layerId));
    }
  });
}

/**
 * Render a layer only when it is VISIBLE and its data changed. Hidden layers
 * are never rendered (no markers created) — this avoids churning thousands of
 * markers for toggled-off feeds.
 */
function renderIfChanged(
  map: MapInstance,
  mode: ViewMode,
  layerId: string,
  data: unknown[] | undefined,
): void {
  if (useTimelineStore.getState().mode === 'historical') return;
  if (!data || lastRendered.get(layerId) === data) return;
  if (!useLayerVisibilityStore.getState().isVisible(layerId)) return;
  lastRendered.set(layerId, data);
  renderLayer(map, mode, layerId, data as Track[], true);
}

/**
 * Re-render visible layers after a 2D↔3D switch. The lifecycle hook recreates
 * the map (and clears markers), so the per-layer render cache is cleared and
 * visible layers are repainted on the new map once it is ready.
 */
function subscribeViewMode(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): () => void {
  let previousMode = useMapStore.getState().viewMode;
  let pending: ReturnType<typeof setTimeout> | null = null;
  return useMapStore.subscribe((state) => {
    if (state.viewMode === previousMode) return;
    previousMode = state.viewMode;
    lastRendered.clear();
    if (pending) clearTimeout(pending);
    pending = setTimeout(() => {
      if (useTimelineStore.getState().mode === 'live') renderVisibleLayers(mapRef, modeRef);
    }, 800);
  });
}

/** React to visibility toggles: render current data on show, hide on hide. */
function subscribeVisibility(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): () => void {
  let previous = new Map(useLayerVisibilityStore.getState().visible);
  return useLayerVisibilityStore.subscribe((state) => {
    const map = mapRef.current;
    if (!map) return;
    for (const [layerId, visible] of state.visible) {
      if (visible === previous.get(layerId)) continue;
      applyVisibilityChange(map, modeRef.current, layerId, visible);
    }
    previous = new Map(state.visible);
  });
}

/** Re-render visible track layers when a classification override changes (recolors markers). */
function subscribeClassification(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): () => void {
  return useTrackClassificationStore.subscribe(() => renderVisibleLayers(mapRef, modeRef));
}

/** Re-render visible track layers when the type-filter selection changes (shows/hides sub-types). */
function subscribeFilter(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): () => void {
  return useTrackFilterStore.subscribe(() => renderVisibleLayers(mapRef, modeRef));
}

/**
 * Force-render every visible track layer from current plugin data. Bypasses the
 * `renderIfChanged` reference short-circuit so classification/filter changes on
 * unchanged data still re-render.
 */
function renderVisibleLayers(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): void {
  const map = mapRef.current;
  if (!map) return;
  for (const layerId of trackLayerIds()) {
    if (!useLayerVisibilityStore.getState().isVisible(layerId)) continue;
    const data = usePluginDataStore.getState().data.get(layerId);
    if (data) renderLayer(map, modeRef.current, layerId, data as Track[], true);
  }
}

/**
 * Re-render visible viewport-culled layers from current data. Called by the
 * viewport-culling hook when the camera settles, so the newly visible region
 * re-runs the cull (only culled layers depend on the camera — sparse layers are
 * left alone to avoid churn).
 */
export function reRenderVisibleTrackLayers(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): void {
  const map = mapRef.current;
  if (!map) return;
  for (const layer of trackLayers()) {
    if (!layer.definition.viewportCulled) continue;
    if (!useLayerVisibilityStore.getState().isVisible(layer.fullId)) continue;
    const data = usePluginDataStore.getState().data.get(layer.fullId);
    if (data) renderLayer(map, modeRef.current, layer.fullId, data as Track[], true);
  }
}

/** Render a layer's current data when shown; hide its markers when hidden. */
function applyVisibilityChange(
  map: MapInstance,
  mode: ViewMode,
  layerId: string,
  visible: boolean,
): void {
  if (!visible) {
    setLayerVisibility(mode, layerId, false);
    return;
  }
  const data = usePluginDataStore.getState().data.get(layerId);
  if (!data) return;
  lastRendered.set(layerId, data);
  renderLayer(map, mode, layerId, data as Track[], true);
}
